import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  XCircle,
  PlayCircle,
  Plus,
  Rocket,
} from "lucide-react";
import { EnhancedDeployment, DeploymentStatus } from "../api/client";
import { titleCase } from "../lib/utils";

const statusIcons: Record<DeploymentStatus, React.ReactNode> = {
  draft: <Clock className="w-4 h-4 text-gray-400" />,
  pending_approval: <Clock className="w-4 h-4 text-yellow-500" />,
  approved: <CheckCircle2 className="w-4 h-4 text-blue-500" />,
  rejected: <XCircle className="w-4 h-4 text-red-500" />,
  deploying: <PlayCircle className="w-4 h-4 text-purple-500" />,
  completed: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  success: <CheckCircle2 className="w-4 h-4 text-green-500" />,
  failed: <XCircle className="w-4 h-4 text-red-500" />,
  cancelled: <XCircle className="w-4 h-4 text-gray-400" />,
};

const statusLabels: Record<DeploymentStatus, string> = {
  draft: "Draft",
  pending_approval: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  deploying: "Deploying",
  completed: "Completed",
  success: "Success",
  failed: "Failed",
  cancelled: "Cancelled",
};

interface ReleaseCycleDeploymentMatrixProps {
  deployments: EnhancedDeployment[];
  environments: {
    id: string;
    name: string;
    slug: string;
    is_production: boolean;
  }[];
  onCreateDeployment?: (serviceId: string, environmentId: string) => void;
}

export default function ReleaseCycleDeploymentMatrix({
  deployments,
  environments,
  onCreateDeployment,
}: ReleaseCycleDeploymentMatrixProps) {
  const navigate = useNavigate();

  // Group deployments by service
  const serviceMap = new Map<
    string,
    {
      id: string;
      name: string;
      slug: string;
      deployments: Map<string, EnhancedDeployment[]>;
    }
  >();

  deployments.forEach((dep) => {
    const serviceId = dep.service_id;
    const serviceName = dep.service_name || "Unknown Service";
    const serviceSlug = dep.service_slug || "";
    const envId = dep.environment_id;

    if (!serviceMap.has(serviceId)) {
      serviceMap.set(serviceId, {
        id: serviceId,
        name: serviceName,
        slug: serviceSlug,
        deployments: new Map(),
      });
    }

    const service = serviceMap.get(serviceId)!;
    if (!service.deployments.has(envId)) {
      service.deployments.set(envId, []);
    }
    service.deployments.get(envId)!.push(dep);
  });

  // Sort services by name
  const services = Array.from(serviceMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  // Sort environments: non-prod first, then prod
  const sortedEnvironments = [...environments].sort((a, b) => {
    if (a.is_production !== b.is_production) {
      return a.is_production ? 1 : -1;
    }
    return a.name.localeCompare(b.name);
  });

  if (services.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
        <Rocket className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No deployments in this release cycle</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-r border-gray-200">
                Service
              </th>
              {sortedEnvironments.map((env) => (
                <th
                  key={env.id}
                  className={`px-4 py-3 text-center text-xs font-medium uppercase tracking-wider border-b border-gray-200 ${
                    env.is_production
                      ? "bg-red-50 text-red-700"
                      : "text-gray-500"
                  }`}
                >
                  {env.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {services.map((service) => (
              <tr key={service.id} className="hover:bg-gray-50">
                <td className="px-4 py-3 border-r border-gray-200">
                  <div className="font-medium text-gray-900">
                    {titleCase(service.name)}
                  </div>
                  <div className="text-xs text-gray-500">{service.slug}</div>
                </td>
                {sortedEnvironments.map((env) => {
                  const envDeployments = service.deployments.get(env.id) || [];
                  const latestDeployment =
                    envDeployments.length > 0
                      ? envDeployments.sort(
                          (a, b) =>
                            new Date(b.created_at).getTime() -
                            new Date(a.created_at).getTime(),
                        )[0]
                      : null;

                  return (
                    <td
                      key={env.id}
                      className={`px-2 py-2 text-center ${
                        env.is_production ? "bg-red-50/30" : ""
                      }`}
                    >
                      {latestDeployment ? (
                        <div className="space-y-1">
                          {/* Latest deployment */}
                          <button
                            onClick={() =>
                              navigate(`/deployments/${latestDeployment.id}`)
                            }
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                              latestDeployment.enhanced_status === "completed"
                                ? "bg-green-100 text-green-700 hover:bg-green-200"
                                : latestDeployment.enhanced_status === "failed"
                                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                                  : latestDeployment.enhanced_status ===
                                      "deploying"
                                    ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
                                    : latestDeployment.enhanced_status ===
                                        "approved"
                                      ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                                      : latestDeployment.enhanced_status ===
                                          "pending_approval"
                                        ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            {
                              statusIcons[
                                latestDeployment.enhanced_status ||
                                  latestDeployment.status ||
                                  "draft"
                              ]
                            }
                            <span className="font-mono">
                              {latestDeployment.version}
                            </span>
                          </button>

                          {/* Sequence info - only show for non-production */}
                          {latestDeployment.sequence_type &&
                            !env.is_production && (
                              <div className="text-xs text-gray-400">
                                {latestDeployment.sequence_type.toUpperCase()}
                                {latestDeployment.sequence_number &&
                                  ` #${latestDeployment.sequence_number}`}
                              </div>
                            )}

                          {/* Multiple deployments indicator */}
                          {envDeployments.length > 1 && (
                            <div className="text-xs text-gray-400">
                              +{envDeployments.length - 1} more
                            </div>
                          )}
                        </div>
                      ) : onCreateDeployment ? (
                        <button
                          onClick={() => onCreateDeployment(service.id, env.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                        >
                          <Plus className="w-3 h-3" />
                          Add
                        </button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex flex-wrap gap-4 text-xs">
          <span className="text-gray-500 font-medium">Status:</span>
          {(Object.keys(statusLabels) as DeploymentStatus[]).map((status) => (
            <span
              key={status}
              className="flex items-center gap-1 text-gray-600"
            >
              {statusIcons[status]}
              {statusLabels[status]}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
